import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { ScenarioActiveOption } from 'features/residents/models/scenario-active-option';
import AlarmActiveSelect from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/alarm-active-select';
import {
  AlarmScenarioConfigurationProps,
  useAlarmScenarioConfiguration,
} from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/alarm-scenario-configuration/hooks';
import ScenarioTitleRow from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/scenario-title-row';
import TimeFrames from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/time-frames/time-frames';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import styles from './alarm-scenario-configuration.module.scss';

type Props = AlarmScenarioConfigurationProps & {
  scenarioTitle: string;
  situationalMenuItem?: React.ReactNode;
};

const AlarmScenarioConfiguration = (props: Readonly<Props>): React.JSX.Element => {
  const { scenarioTitle, index, situationalOptionEnabled, situationalMenuItem } = props;

  const { t } = useTranslation('residentsSettings');

  const {
    detailEditState: { viewingMode },
  } = use(ResidentSettingsProviderContext);

  const { configuration, timeFrameValuesChanged, onAlarmActiveChanged, alarmTimeoutChanged, alarmTimeout, determineSelectedActiveOption } =
    useAlarmScenarioConfiguration(props);

  const alarmTimeOutOptions = [
    ...(situationalOptionEnabled ? [situationalMenuItem] : []),
    <MenuItem data-testid={`alarm-timeout-item-direct`} key={`alarm-timeout-item-direct`} value={0}>
      {t('scenarioConfiguration.alarmTimeOutDirect')}
    </MenuItem>,
    <MenuItem data-testid={`alarm-timeout-item-one-minute`} key={`alarm-timeout-item-one-minute`} value={60}>
      {t('scenarioConfiguration.alarmTimeOutOneMinute')}
    </MenuItem>,
    <MenuItem data-testid={`alarm-timeout-item-five-minutes`} key={`alarm-timeout-item-five-minutes`} value={300}>
      {t('scenarioConfiguration.alarmTimeOutFiveMinutes')}
    </MenuItem>,
    <MenuItem data-testid={`alarm-timeout-item-fifteen-minutes`} key={`alarm-timeout-item-fifteen-minutes`} value={900}>
      {t('scenarioConfiguration.alarmTimeOutFifteenMinutes')}
    </MenuItem>,
    <MenuItem data-testid={`alarm-timeout-item-thirty-minutes`} key={`alarm-timeout-item-thirty-minutes`} value={1800}>
      {t('scenarioConfiguration.alarmTimeOutThirtyMinutes')}
    </MenuItem>,
    <MenuItem data-testid={`alarm-timeout-item-sixty-minutes`} key={`alarm-timeout-item-sixty-minutes`} value={3600}>
      {t('scenarioConfiguration.alarmTimeOutSixtyMinutes')}
    </MenuItem>,
  ];

  const activeOptions = [
    <MenuItem data-testid={`status-and-alarms-active`} key={`status-and-alarms-active`} value={ScenarioActiveOption.StatusAndAlarms}>
      {t('scenarioConfiguration.statusAndAlarmsActive')}
    </MenuItem>,
    <MenuItem data-testid={`alarm-active`} key={`alarm-active`} value={ScenarioActiveOption.AlarmsOnly}>
      {t('scenarioConfiguration.alarmActive')}
    </MenuItem>,
    <MenuItem data-testid={`status-active`} key={`status-active`} value={ScenarioActiveOption.StatusOnly}>
      {t('scenarioConfiguration.statusActive')}
    </MenuItem>,
  ];

  return (
    <div className={styles.container}>
      <ScenarioTitleRow scenarioTitle={scenarioTitle} description={t('scenarioConfiguration.scenarioDescription')} />
      <div className={styles.alarmActiveRow}>
        <AlarmActiveSelect
          label={t('scenarioConfiguration.alarmActiveLabel')}
          value={determineSelectedActiveOption()}
          options={activeOptions}
          onChange={onAlarmActiveChanged}
          disabled={viewingMode === 'viewing'}
          className={styles.alarmActiveSelect}
        />
      </div>

      {configuration.isAlarmActive && (
        <>
          <TimeFrames
            activeAllDay={configuration.sendAlarmAllDay}
            index={index}
            timeFrames={configuration.timeFrames}
            valuesChanged={timeFrameValuesChanged}
            timeFramesDescription={t('scenarioConfiguration.timeFramesDescriptionAlarm')}
          />

          <div className={styles.alarmRow}>
            <FormControl sx={{ minWidth: '100%' }}>
              <InputLabel id="send-alarm-label">{t('scenarioConfiguration.sendAlarmLabel')}</InputLabel>
              <Select
                inputProps={{ 'data-testid': 'sendAlarmInput' }}
                id="sendAlarmInput"
                variant="outlined"
                className={styles.sendAlarmSelect}
                labelId="send-alarm-label"
                label={t('scenarioConfiguration.sendAlarmLabel')}
                value={alarmTimeout}
                defaultValue={0}
                disabled={viewingMode === 'viewing'}
                onChange={alarmTimeoutChanged}
              >
                {alarmTimeOutOptions}
              </Select>
            </FormControl>
          </div>
        </>
      )}
    </div>
  );
};

export default AlarmScenarioConfiguration;
